import { EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Drawer, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { listPublishTaskRuns } from '../api';
import { formatErrorMessage, resolveErrorMessage } from '../utils/errorMessage';
import { formatShanghaiDateTime } from '../utils/datetime';
import { getAgentTypeLabel } from '../utils/agentExecutionPolicy';
import PublishRunViewer from './PublishRunViewer';

const { Text } = Typography;

const STATUS_COLORS = {
  pending: 'gold',
  running: 'gold',
  success: 'green',
  noop: 'blue',
  failed: 'red',
  canceled: 'default',
};

const STATUS_TEXT = {
  pending: '待执行',
  running: '运行中',
  success: '成功',
  noop: '无操作',
  failed: '失败',
  canceled: '已取消',
};

const statusTag = (status) => {
  const s = String(status || '').toLowerCase();
  return <Tag color={STATUS_COLORS[s] || 'default'}>{STATUS_TEXT[s] || s || '-'}</Tag>;
};

const formatDateTime = (dateTimeString) => {
  return formatShanghaiDateTime(dateTimeString);
};

const PublishRunHistoryDrawer = ({ open, task, onClose }) => {
  const taskId = String(task?.task_id || '').trim();

  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [detailOpen, setDetailOpen] = useState(false);
  const [activeTaskRunId, setActiveTaskRunId] = useState('');

  const refresh = useCallback(async (pageOverride = null, pageSizeOverride = null) => {
    if (!open || !taskId) return;
    setLoading(true);
    try {
      const resp = await listPublishTaskRuns({
        task_id: taskId,
        statuses: statusFilter ? [statusFilter] : undefined,
        page: pageOverride ?? page,
        page_size: pageSizeOverride ?? pageSize,
      });
      if (resp?.status === 'success') {
        setItems(Array.isArray(resp.items) ? resp.items : []);
        setTotal(resp.total || 0);
        setPage(resp.page || (pageOverride ?? page));
        setPageSize(resp.page_size || (pageSizeOverride ?? pageSize));
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '获取运行历史失败'));
      }
    } catch (e) {
      message.error(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [open, page, pageSize, statusFilter, taskId]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setPageSize(10);
    setStatusFilter(undefined);
  }, [open, taskId]);

  useEffect(() => {
    if (!open) return;
    refresh(1, pageSize);
  }, [open, refresh, taskId, pageSize]);

  const columns = useMemo(() => ([
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v) => statusTag(v),
    },
    {
      title: '运行ID',
      dataIndex: 'run_id',
      key: 'run_id',
      width: 420,
      render: (v) => <Text code copyable={{ text: v }}>{v}</Text>,
    },
    {
      title: '开始时间',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 200,
      render: (v) => <Text>{formatDateTime(v)}</Text>,
    },
    {
      title: '结束时间',
      dataIndex: 'ended_at',
      key: 'ended_at',
      width: 200,
      render: (v) => <Text>{formatDateTime(v)}</Text>,
    },
    {
      title: '统计',
      dataIndex: 'stats',
      key: 'stats',
      width: 200,
      render: (s) => {
        const ok = s?.published_count ?? '-';
        const fail = s?.failed_count ?? '-';
        return <Text>成功:{ok} / 失败:{fail}</Text>;
      },
    },
    {
      title: '计划节点',
      key: 'planned_agent',
      width: 180,
      render: (_, row) => {
        const type = String(row?.planned_agent_type || '').trim();
        const id = String(row?.planned_agent_id || '').trim();
        if (!type && !id) return <Text type="secondary">-</Text>;
        return <Text>{getAgentTypeLabel(type)}{id ? ` · ${id}` : ''}</Text>;
      },
    },
    {
      title: '实际节点',
      key: 'actual_agent',
      width: 180,
      render: (_, row) => {
        const type = String(row?.actual_agent_type || '').trim();
        const id = String(row?.actual_agent_id || '').trim();
        if (!type && !id) return <Text type="secondary">-</Text>;
        return <Text>{getAgentTypeLabel(type)}{id ? ` · ${id}` : ''}</Text>;
      },
    },
    {
      title: '切换原因',
      dataIndex: 'switch_reason',
      key: 'switch_reason',
      width: 180,
      render: (v) => <Text>{v || '-'}</Text>,
    },
    {
      title: '错误',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 260,
      render: (v) => {
        const msg = formatErrorMessage(v);
        return (
          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
            {msg || '-'}
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, row) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setActiveTaskRunId(row?.run_id || '');
            setDetailOpen(true);
          }}
        >
          查看
        </Button>
      ),
    },
  ]), []);

  return (
    <>
      <Drawer
        title={`运行历史（${task?.task_name || taskId}）`}
        open={open}
        onClose={onClose}
        width={1100}
        destroyOnClose
        extra={(
          <Space>
            <Select
              style={{ width: 160 }}
              allowClear
              placeholder="状态"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: '待执行', value: 'pending' },
                { label: '运行中', value: 'running' },
                { label: '成功', value: 'success' },
                { label: '无操作', value: 'noop' },
                { label: '失败', value: 'failed' },
                { label: '已取消', value: 'canceled' },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refresh(1, pageSize)} loading={loading}>
              刷新
            </Button>
          </Space>
        )}
      >
        <Table
          size="small"
          rowKey="run_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 1880 }}
          tableLayout="fixed"
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
              refresh(p, ps);
            },
          }}
          locale={{ emptyText: '暂无运行记录' }}
        />
      </Drawer>

      <PublishRunViewer
        open={detailOpen}
        taskRunId={activeTaskRunId}
        onClose={() => setDetailOpen(false)}
      />
    </>
  );
};

export default PublishRunHistoryDrawer;
